/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable jsx-a11y/label-has-for */
import React, { FormEvent, forwardRef, useEffect } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Portal from '../Portal';
import greySearch from '../../images/search-icon_grey.svg';
import blueSearch from '../../images/search-icon_blue.svg';

interface SearchProps {
  open?: boolean;
  placeholder?: string | null;
  tabIndex?: string;
  onClick?: (e: FormEvent<HTMLFormElement>) => void;
}

const Wrapper = styled.div<SearchProps>`
  width: 100%;
  height: 42px;
  position: relative;
  z-index: 5;

  &.header {
    height: 35px;
    max-width: 500px;
    @media only screen and (max-width: 915px) {
      display: none;
    }
  }

  &.main-menu {
    display: block;
    height: 42px;
    margin: 0;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      margin-bottom: 0;
    }

    @media only screen and (min-width: 916px) {
      display: none;
    }
  }
`;

const Form = styled.form`
  position: relative;
  display: flex;
  justify-content: center;
  &.header {
    justify-content: flex-end;
  }

  &.center {
    justify-content: center;
  }

  &.main-menu {
    justify-content: flex-start;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &.main-menu {
      justify-content: center;
    }
  }
`;

const Submit = styled.button<SearchProps>`
  color: transparent;
  border-radius: 30px;
  position: absolute;
  left: 0%;
  height: 42px;
  width: 42px;

  ${Form}.header & {
    height: 35px;
    width: 35px;
  }

  ${Form}.main-menu & {
    height: 42px;
    width: 35px;
  }

  @media only screen and (min-width: 916px) {
    display: ${({ open }) => (open ? 'block' : 'none')};
  }
`;

const SearchContainer = styled.div<SearchProps>`
  width: 100%;
  max-width: 100%;
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${Form}.main-menu & {
    max-width: 100%;
  }

  fieldset {
    width: 100%;
    label {
      font-size: 1rem;
      font-weight: ${themeGet('fontWeights.normal')};
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
  }

  .search-form {
    width: 100%;
    max-width: 100%;
    position: relative;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    ${Form}.main-menu & {
      max-width: 100%;
      margin: 0;
    }
  }
`;

const Search = styled.input<SearchProps>`
  border-radius: 30px;
  box-shadow: 0 2px 12px 0 rgba(180, 183, 189, 0.12);
  background: ${themeGet('colors.white')} url(${blueSearch}) no-repeat 6px
    center;
  color: transparent;
  width: 100%;
  transition: all 0.5s ease-in-out;
  cursor: pointer;
  width: 42px;
  max-width: 100%;
  height: 42px;
  background: ${themeGet('colors.white')} url(${greySearch}) no-repeat 18px
    center;
  box-shadow: 0 2px 4px 0 rgba(47, 47, 47, 0.03);
  border-radius: 30px;
  padding: 10px;
  line-height: 1;

  @-moz-document url-prefix() {
    padding: 20px;
  }

  &::placeholder {
    color: transparent;
    transition: all 0.75s;
    font-size: 18px;
    line-height: 2.33;
    font-weight: ${themeGet('fontWeights.normal')};
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    overflow: visible;
  }

  &.is-open {
    padding-left: 60px;
    box-shadow: 0 2px 12px 0 rgba(180, 183, 189, 0.12);
    color: ${themeGet('colors.primary-text')};
    width: 100%;
    background: ${themeGet('colors.white')} url(${blueSearch}) no-repeat 18px
      center;
    cursor: auto;
    &::placeholder {
      color: ${themeGet('colors.secondary-text')};
    }
  }

  &.header {
    border: 1px solid #ebedeb;
    height: 35px;
    width: 35px;
    background: ${themeGet('colors.white')} url(${greySearch}) no-repeat 6px
      center;
    &::placeholder {
      font-size: 16px;
      line-height: 1;
    }
    &.is-open {
      color: ${themeGet('colors.primary-text')};
      padding-left: 35px;
      background: ${themeGet('colors.white')} url(${blueSearch}) no-repeat 6px
        center;
      width: 100%;
    }
  }

  &.main-menu {
    height: 42px;
    width: 35px;
    max-width: 100%;
    border: solid 1px ${themeGet('colors.border-grey')};
    background: ${themeGet('colors.pale-grey')} url(${greySearch}) no-repeat 6px
      center;
    &::placeholder {
      font-size: 16px;
      line-height: 1;
    }
    &.is-open,
    &:focus {
      color: ${themeGet('colors.primary-text')};
      padding-left: 35px;
      background: ${themeGet('colors.pale-grey')} url(${blueSearch}) no-repeat
        6px center;
      width: 100%;
    }
  }
`;

const Overlay = styled.div<SearchProps>`
  position: fixed;
  pointer-events: all;
  height: 100vh;
  width: 100vw;
  left: 0;
  top: 0;
  display: ${({ open }) => (open ? 'block' : 'none')};
  color: ${({ open }) => (open ? 'red' : 'blue')};
`;

interface SearchBarProps {
  children?: React.ReactNode;
  id?: string;
  className?: string;
  type?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  arialabel?: string;
  predictiveSearch?: boolean;
}

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>((
  {
    id,
    children = null,
    className = null,
    type = "defaultType",
    placeholder = null,
    value = '',
    arialabel,
    onChange = () => { },
    onSubmit = () => { },
    open = false,
    setOpen = () => { },
    predictiveSearch = false,
  },
  ref,
) => {
  const expanded = type === 'is-open' && !predictiveSearch;

  useEffect(() => {
    if (value.length) setOpen(true);
  }, [value, setOpen]);

  return (
    <Wrapper className={[className, type].join(' ')}>
      <Form
        className={type}
        role="search"
        onSubmit={onSubmit}
        aria-label={arialabel}
        action="#"
      >
        <SearchContainer>
          {expanded ? (
            <fieldset>
              <label htmlFor={id}>{placeholder}</label>
              <div className="search-form-1">
                <Search
                  id={id}
                  type="search"
                  placeholder={placeholder}
                  className={[type, 'searchable-marker'].join(' ')}
                  value={value}
                  onChange={onChange}
                  onClick={() => setOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setOpen(true);
                    } else if (e.key === 'Escape') {
                      setOpen(false);
                    }
                  }}
                  tabIndex="0"
                  ref={ref}
                  aria-live="polite"
                  pattern="[^()/><\][\\\x22,;|]+"
                  autoComplete="off"
                  enterKeyHint="search"
                />
                <Submit
                  type="submit"
                  className="searchable-marker"
                  aria-label="Submit Search"
                  open
                  onClick={onSubmit}
                />
              </div>
            </fieldset>
          ) : (
            <fieldset>
              <label htmlFor={id}>{placeholder}</label>
              <div className="search-form">
                <Search
                  id={id}
                  type="search"
                  placeholder={placeholder}
                  className={[
                    type,
                    open ? 'is-open' : '',
                    'searchable-marker',
                  ].join(' ')}
                  onClick={() => setOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setOpen(true);
                    } else if (e.key === 'Escape') {
                      setOpen(false);
                    }
                  }}
                  tabIndex="0"
                  value={value}
                  onChange={onChange}
                  aria-label={arialabel}
                  ref={ref}
                  autoComplete="off"
                  enterKeyHint="search"
                />
                <Submit
                  type="submit"
                  className="searchable-marker"
                  aria-label="Submit Search"
                  open={open}
                  onClick={onSubmit}
                />
              </div>
            </fieldset>
          )}
          {children}
        </SearchContainer>
      </Form>
      {!expanded && (
        <Portal>
          <Overlay open={open} />
        </Portal>
      )}
    </Wrapper>
  );
});

export default SearchBar;
